import { AxiosError } from 'axios';

import { api } from '@/lib/api-client';

import type { SendMessageResponse } from '../types';
import type { ApiResponse } from '@nohara-dxdev/oidc-core';

export const FLAGGED_REASONS = ['PROMPT_INJECTION', 'PROFANITY', 'NSFW', 'OFF_TOPIC'] as const;
export type FlaggedReason = (typeof FLAGGED_REASONS)[number] | 'UNKNOWN';

const isFlaggedReason = (value: unknown): value is (typeof FLAGGED_REASONS)[number] =>
  typeof value === 'string' && (FLAGGED_REASONS as readonly string[]).includes(value);

export class FlaggedMessageError extends Error {
  readonly reason: FlaggedReason;
  constructor(reason: FlaggedReason) {
    super(`Message flagged: ${reason}`);
    this.name = 'FlaggedMessageError';
    this.reason = reason;
  }
}

/** MT1-1430: raised when the message exceeds the org's configured max length (HTTP 400). */
export class MessageTooLongError extends Error {
  readonly maxLength?: number;
  constructor(maxLength?: number) {
    super('Message exceeds the maximum length');
    this.name = 'MessageTooLongError';
    this.maxLength = maxLength;
  }
}

/** MT1-1430: raised when the org has reached its monthly token budget (HTTP 429). */
export class MonthlyTokenLimitError extends Error {
  constructor() {
    super('Monthly AI token limit reached');
    this.name = 'MonthlyTokenLimitError';
  }
}

export const sendMessage = async ({
  orgId,
  projectId,
  sessionId,
  externalId,
  content,
  attachments,
  debug,
  parentId,
  locale = 'ja',
}: {
  orgId: string;
  projectId: string;
  sessionId: string;
  externalId?: string;
  content: string;
  attachments?: { key: string; contentType: string }[];
  debug?: boolean;
  parentId?: string;
  locale?: 'en' | 'ja';
}): Promise<SendMessageResponse> => {
  try {
    const { data } = await api.post<unknown, ApiResponse<SendMessageResponse>>(
      `/orgs/${orgId}/projects/${projectId}/ai-agent/sessions/${sessionId}/messages`,
      {
        externalId,
        content,
        attachments,
        locale,
        ...(debug ? { debug: true } : {}),
        ...(parentId ? { parentId } : {}),
      },
    );

    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const body = error.response?.data as
        | { code?: string; reason?: string; message?: string; maxLength?: number }
        | undefined;

      if (status === 422 && body?.code === 'MESSAGE_FLAGGED') {
        throw new FlaggedMessageError(isFlaggedReason(body.reason) ? body.reason : 'UNKNOWN');
      }
      // MT1-1430: usage-limit rejections from the send-message endpoint.
      if (status === 429) {
        throw new MonthlyTokenLimitError();
      }
      if (status === 400 && (body?.message ?? '').includes('AI_AGENT_MESSAGE_TOO_LONG')) {
        throw new MessageTooLongError(body?.maxLength);
      }
    }
    throw error;
  }
};
